import type { GeraeteInfo } from './vertrag';

/**
 * Sicherer Gerätespeicher für Aktivierungsgeheimnis und Gerätezugang (Geräteaktivierung V2).
 *
 * - **Browser/Windows:** eigene IndexedDB `bilanx-geraet` (getrennt von `bilanx-kassa`, damit
 *   deren Version und Upgrade-Zaun unberührt bleiben). Ein AES-GCM-256-Schlüssel wird als
 *   **nicht exportierbarer** `CryptoKey` dort abgelegt; jeder Eintrag ist damit verschlüsselt und
 *   über `additionalData` an seinen Namen gebunden. Grenze: Code auf derselben Origin (z. B. XSS)
 *   kann den Schlüssel benutzen, aber nicht auslesen oder auf ein anderes Gerät kopieren.
 * - **Android (SUNMI-Hülle):** Gerätezugang über `window.BilanxGeraet.speichereZugang/leseZugang`
 *   im Android-Keystore (Schnittstelle von SUNMI 3); die laufende Sitzung bleibt im WebView-Speicher.
 * - **Arbeitsspeicher:** nur für Tests.
 *
 * Ohne sicheren Speicher (kein IndexedDB/WebCrypto, z. B. unsicherer Kontext http) gibt es keine
 * Aktivierung V2; die bisherige Einrichtung mit Kassenschlüssel bleibt nutzbar.
 */
export interface SichererSpeicher {
  readonly art: 'webcrypto' | 'android-keystore' | 'arbeitsspeicher';
  /** Gespeicherter Wert oder null; `TresorUnlesbar`, wenn ein Eintrag nicht entschlüsselbar ist. */
  lese(name: string): Promise<string | null>;
  schreibe(name: string, wert: string): Promise<void>;
  loesche(name: string): Promise<void>;
}

/** Eintrag vorhanden, aber nicht lesbar (Schlüssel verloren oder Daten verändert). */
export class TresorUnlesbar extends Error {
  override readonly name = 'TresorUnlesbar';
  constructor(eintrag: string) {
    super(`Gespeicherter Eintrag „${eintrag}“ ist nicht lesbar – Gerät bitte neu aktivieren`);
  }
}

const NAME_MUSTER = /^[a-z0-9][a-z0-9.-]{0,63}$/;

function pruefeName(name: string): void {
  if (!NAME_MUSTER.test(name)) {
    throw new RangeError('Ungültiger Eintragsname');
  }
}

/** Schlüssel-Wert-Ablage für strukturierte Werte (CryptoKey, ArrayBuffer). */
export interface Rohablage {
  hole(schluessel: string): Promise<unknown>;
  lege(schluessel: string, wert: unknown): Promise<void>;
  /** Legt nur an, wenn der Schlüssel noch fehlt (atomar); false = schon vorhanden. */
  legeFallsFrei(schluessel: string, wert: unknown): Promise<boolean>;
  entferne(schluessel: string): Promise<void>;
}

export class ArbeitsspeicherAblage implements Rohablage {
  readonly daten = new Map<string, unknown>();
  hole(schluessel: string): Promise<unknown> {
    return Promise.resolve(this.daten.get(schluessel));
  }
  lege(schluessel: string, wert: unknown): Promise<void> {
    this.daten.set(schluessel, wert);
    return Promise.resolve();
  }
  legeFallsFrei(schluessel: string, wert: unknown): Promise<boolean> {
    if (this.daten.has(schluessel)) {
      return Promise.resolve(false);
    }
    this.daten.set(schluessel, wert);
    return Promise.resolve(true);
  }
  entferne(schluessel: string): Promise<void> {
    this.daten.delete(schluessel);
    return Promise.resolve();
  }
}

export const TRESOR_DB = 'bilanx-geraet';
const TRESOR_STORE = 'tresor';

/** IndexedDB-Ablage (eigene Datenbank, Version 1, ein Objektspeicher). */
export class IndexedDbAblage implements Rohablage {
  private db: Promise<IDBDatabase> | null = null;

  constructor(private readonly idb: IDBFactory) {}

  private oeffne(): Promise<IDBDatabase> {
    this.db ??= new Promise<IDBDatabase>((loese, lehneAb) => {
      const req = this.idb.open(TRESOR_DB, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(TRESOR_STORE)) {
          req.result.createObjectStore(TRESOR_STORE);
        }
      };
      req.onsuccess = () => {
        const d = req.result;
        d.onversionchange = () => {
          d.close();
          this.db = null;
        };
        loese(d);
      };
      req.onerror = () => {
        this.db = null;
        lehneAb(req.error ?? new Error('IndexedDB nicht verfügbar'));
      };
    });
    return this.db;
  }

  private async tx<T>(
    modus: IDBTransactionMode,
    arbeit: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const d = await this.oeffne();
    return new Promise<T>((loese, lehneAb) => {
      const t = d.transaction(TRESOR_STORE, modus);
      const req = arbeit(t.objectStore(TRESOR_STORE));
      // Erst nach dem Abschluss der Transaktion gilt ein Schreibvorgang als dauerhaft
      t.oncomplete = () => {
        loese(req.result);
      };
      t.onerror = () => {
        lehneAb(t.error ?? new Error('IndexedDB-Fehler'));
      };
      t.onabort = () => {
        lehneAb(t.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
      };
    });
  }

  hole(schluessel: string): Promise<unknown> {
    return this.tx('readonly', (s) => s.get(schluessel));
  }
  async lege(schluessel: string, wert: unknown): Promise<void> {
    await this.tx('readwrite', (s) => s.put(wert, schluessel));
  }
  async legeFallsFrei(schluessel: string, wert: unknown): Promise<boolean> {
    const d = await this.oeffne();
    return new Promise<boolean>((loese, lehneAb) => {
      const t = d.transaction(TRESOR_STORE, 'readwrite');
      let angelegt = true;
      // add() statt put(): legt zwei Fenster gleichzeitig an, gewinnt genau eines (C12)
      const req = t.objectStore(TRESOR_STORE).add(wert, schluessel);
      req.onerror = (ev) => {
        if (req.error?.name === 'ConstraintError') {
          angelegt = false;
          ev.preventDefault(); // Transaktion nicht abbrechen
        }
      };
      t.oncomplete = () => {
        loese(angelegt);
      };
      t.onerror = () => {
        lehneAb(t.error ?? new Error('IndexedDB-Fehler'));
      };
      t.onabort = () => {
        lehneAb(t.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
      };
    });
  }
  async entferne(schluessel: string): Promise<void> {
    await this.tx('readwrite', (s) => s.delete(schluessel));
  }
}

const SCHLUESSEL_EINTRAG = 'schluessel:v1';

interface Verschluesselt {
  v: 1;
  iv: Uint8Array<ArrayBuffer>;
  daten: ArrayBuffer;
}

function istVerschluesselt(x: unknown): x is Verschluesselt {
  return (
    typeof x === 'object' &&
    x !== null &&
    (x as Record<string, unknown>).v === 1 &&
    (x as Verschluesselt).iv instanceof Uint8Array &&
    (x as Verschluesselt).daten instanceof ArrayBuffer
  );
}

function istCryptoKey(x: unknown): x is CryptoKey {
  return (
    typeof x === 'object' &&
    x !== null &&
    'algorithm' in x &&
    'extractable' in x &&
    'usages' in x &&
    (x as CryptoKey).type === 'secret'
  );
}

/** Verschlüsselter Speicher mit nicht exportierbarem AES-GCM-Schlüssel. */
export class WebCryptoSpeicher implements SichererSpeicher {
  readonly art = 'webcrypto' as const;
  private schluesselVersprechen: Promise<CryptoKey> | null = null;

  constructor(
    private readonly ablage: Rohablage,
    private readonly subtle: SubtleCrypto = crypto.subtle,
    private readonly zufall: (n: number) => Uint8Array<ArrayBuffer> = (n) =>
      crypto.getRandomValues(new Uint8Array(n)),
  ) {}

  private schluessel(): Promise<CryptoKey> {
    this.schluesselVersprechen ??= (async () => {
      const vorhanden = await this.ablage.hole(SCHLUESSEL_EINTRAG);
      if (istCryptoKey(vorhanden)) {
        return vorhanden;
      }
      const neu = await this.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ]);
      // C12: nie überschreiben. Zwei Fenster gleichzeitig → genau eines legt an, das andere
      // übernimmt dessen Schlüssel; so wird nie mit einem später ersetzten Schlüssel verschlüsselt.
      if (await this.ablage.legeFallsFrei(SCHLUESSEL_EINTRAG, neu)) {
        return neu;
      }
      const gespeichert = await this.ablage.hole(SCHLUESSEL_EINTRAG);
      if (!istCryptoKey(gespeichert)) {
        // Eintrag belegt, aber kein Schlüssel: nicht ersetzen (vorhandene Daten blieben unlesbar)
        throw new TresorUnlesbar('schluessel');
      }
      return gespeichert;
    })().catch((e: unknown) => {
      this.schluesselVersprechen = null;
      throw e;
    });
    return this.schluesselVersprechen;
  }

  async lese(name: string): Promise<string | null> {
    pruefeName(name);
    const eintrag = await this.ablage.hole(`e:${name}`);
    if (eintrag === undefined || eintrag === null) {
      return null;
    }
    if (!istVerschluesselt(eintrag)) {
      throw new TresorUnlesbar(name);
    }
    const vorhanden = await this.ablage.hole(SCHLUESSEL_EINTRAG);
    if (!istCryptoKey(vorhanden)) {
      // Schlüssel fehlt: nie einen neuen anlegen und so tun, als wäre nichts gespeichert
      throw new TresorUnlesbar(name);
    }
    try {
      const klar = await this.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: new Uint8Array(eintrag.iv),
          additionalData: new TextEncoder().encode(name),
        },
        vorhanden,
        eintrag.daten,
      );
      return new TextDecoder().decode(klar);
    } catch {
      throw new TresorUnlesbar(name);
    }
  }

  async schreibe(name: string, wert: string): Promise<void> {
    pruefeName(name);
    const k = await this.schluessel();
    const iv = this.zufall(12);
    const daten = await this.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(name) },
      k,
      new TextEncoder().encode(wert),
    );
    const eintrag: Verschluesselt = { v: 1, iv, daten };
    await this.ablage.lege(`e:${name}`, eintrag);
  }

  async loesche(name: string): Promise<void> {
    pruefeName(name);
    await this.ablage.entferne(`e:${name}`);
  }
}

/** Eintrag, den die Android-Hülle im Keystore hält (ein Zugang je Origin). */
export const ANDROID_ZUGANG_EINTRAG = 'aktivierung.zugang';

/**
 * Gründe, die die Hülle annimmt (SUNMI 3): Löschen nur bei eindeutigem Widerruf (401
 * `geraet_widerrufen` mit `geraetWiderrufen: true`) oder ausdrücklicher Neuaktivierung. Eine
 * Sperre behält den Zugang (AV1-Vertrag).
 */
export type ZugangLoeschGrund = 'widerrufen' | 'neuaktivierung';

/**
 * Zugangs-Schnittstelle der SUNMI-Hülle (`docs/android/aktivierung-bridge.md`, Branch
 * `sunmi3-sicherheit`). Fehler kommen als `reject(Error)` mit `.code`.
 */
export interface AndroidZugangBridge {
  speichereZugang(zugangJson: string): Promise<unknown>;
  leseZugang(): Promise<{ zugang: string | null }>;
  loescheZugang(grund: ZugangLoeschGrund): Promise<unknown>;
}

export function istAndroidZugangBridge(x: unknown): x is AndroidZugangBridge {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as Partial<AndroidZugangBridge>).speichereZugang === 'function' &&
    typeof (x as Partial<AndroidZugangBridge>).leseZugang === 'function' &&
    typeof (x as Partial<AndroidZugangBridge>).loescheZugang === 'function'
  );
}

function bridgeCode(e: unknown): string | null {
  if (typeof e === 'object' && e !== null) {
    const c = (e as Record<string, unknown>).code;
    return typeof c === 'string' ? c : null;
  }
  return null;
}

/**
 * Android-Hülle: der **Gerätezugang** liegt im Android-Keystore der App, alle anderen Einträge
 * (laufende Aktivierungssitzung) im sicheren Speicher der WebView. Die Hülle nimmt laut ihrer
 * Schnittstelle nur den fertigen Zugang nach erfolgreichem Abholen an.
 *
 * - `zugang_unlesbar` → `TresorUnlesbar` (neu aktivieren).
 * - `nicht_erlaubt` (keine bestätigte Kassen-Adresse) → ab dann Browser-Weg auch für den Zugang.
 * - Andere Fehler (Keystore, Zeitüberschreitung) werden weitergereicht, nie als „kein Zugang“.
 * - `loesche(Zugang)` ruft `loescheZugang('neuaktivierung')` und ist nur für eine ausdrückliche
 *   Neuaktivierung gedacht; bei Widerruf (401 `geraet_widerrufen`, `geraetWiderrufen: true`)
 *   `loescheZugang('widerrufen')` – nie bei Sperre, 429/5xx/Netzfehler oder Bediener-Abmeldung.
 */
export class AndroidKeystoreSpeicher implements SichererSpeicher {
  readonly art = 'android-keystore' as const;
  private bridgeErlaubt = true;

  constructor(
    private readonly bridge: AndroidZugangBridge,
    private readonly webview: SichererSpeicher,
  ) {}

  private istZugang(name: string): boolean {
    return name === ANDROID_ZUGANG_EINTRAG && this.bridgeErlaubt;
  }

  private async ueberBridge<T>(arbeit: () => Promise<T>, ersatz: () => Promise<T>): Promise<T> {
    try {
      return await arbeit();
    } catch (e) {
      if (bridgeCode(e) === 'nicht_erlaubt') {
        this.bridgeErlaubt = false;
        return ersatz();
      }
      throw e;
    }
  }

  async lese(name: string): Promise<string | null> {
    pruefeName(name);
    if (!this.istZugang(name)) {
      return this.webview.lese(name);
    }
    let antwort: unknown;
    try {
      antwort = await this.ueberBridge(
        () => this.bridge.leseZugang(),
        async () => ({ zugang: await this.webview.lese(name) }),
      );
    } catch (e) {
      if (bridgeCode(e) === 'zugang_unlesbar') {
        throw new TresorUnlesbar(name);
      }
      throw e;
    }
    const wert =
      typeof antwort === 'object' && antwort !== null
        ? (antwort as Record<string, unknown>).zugang
        : undefined;
    if (wert === null) {
      return null;
    }
    if (typeof wert !== 'string') {
      throw new TresorUnlesbar(name);
    }
    return wert;
  }

  async schreibe(name: string, wert: string): Promise<void> {
    pruefeName(name);
    if (!this.istZugang(name)) {
      await this.webview.schreibe(name, wert);
      return;
    }
    await this.ueberBridge(
      () => this.bridge.speichereZugang(wert),
      () => this.webview.schreibe(name, wert),
    );
  }

  async loesche(name: string): Promise<void> {
    pruefeName(name);
    if (!this.istZugang(name)) {
      await this.webview.loesche(name);
      return;
    }
    await this.loescheZugang('neuaktivierung');
  }

  /** Löscht Zugang und Geräteschlüssel der Hülle; nur bei eindeutigem Widerruf oder Neuaktivierung. */
  async loescheZugang(grund: ZugangLoeschGrund): Promise<void> {
    await this.ueberBridge(
      () => this.bridge.loescheZugang(grund),
      () => this.webview.loesche(ANDROID_ZUGANG_EINTRAG),
    );
  }
}

/** Geräteangaben der Hülle (`geraeteInfo()`), ohne Seriennummer – die kann ohnehin fehlen. */
export interface AndroidGeraeteInfoBridge {
  geraeteInfo(): Promise<unknown>;
}

function kuerze(x: unknown, max: number): string | undefined {
  if (typeof x !== 'string') {
    return undefined;
  }
  const t = x.trim().slice(0, max);
  return t === '' ? undefined : t;
}

/**
 * Liest Hersteller/Modell/App-Version aus `window.BilanxGeraet.geraeteInfo()` für die Startanfrage.
 * Ohne Bridge oder bei Fehlern gilt `ersatz`.
 */
export async function ermittleGeraeteInfo(
  bilanxGeraet: unknown,
  ersatz: GeraeteInfo,
): Promise<GeraeteInfo> {
  const g = bilanxGeraet as Partial<AndroidGeraeteInfoBridge> | null | undefined;
  if (typeof g?.geraeteInfo !== 'function') {
    return ersatz;
  }
  let roh: unknown;
  try {
    roh = await g.geraeteInfo();
  } catch {
    return ersatz;
  }
  if (typeof roh !== 'object' || roh === null) {
    return ersatz;
  }
  const r = roh as Record<string, unknown>;
  const hersteller = kuerze(r.hersteller, 40);
  const modell = kuerze([hersteller, kuerze(r.modell, 40)].filter(Boolean).join(' '), 80);
  const appVersion = kuerze(r.appVersion, 40);
  const info: GeraeteInfo = {
    plattform: hersteller?.toUpperCase() === 'SUNMI' ? 'sunmi' : 'android',
  };
  if (modell !== undefined) {
    info.modell = modell;
  }
  if (appVersion !== undefined) {
    info.appVersion = appVersion;
  }
  return info;
}

export class ArbeitsspeicherTresor implements SichererSpeicher {
  readonly art = 'arbeitsspeicher' as const;
  readonly daten = new Map<string, string>();
  lese(name: string): Promise<string | null> {
    pruefeName(name);
    return Promise.resolve(this.daten.get(name) ?? null);
  }
  schreibe(name: string, wert: string): Promise<void> {
    pruefeName(name);
    this.daten.set(name, wert);
    return Promise.resolve();
  }
  loesche(name: string): Promise<void> {
    pruefeName(name);
    this.daten.delete(name);
    return Promise.resolve();
  }
}

export interface SpeicherUmgebung {
  BilanxGeraet?: unknown;
  indexedDB?: IDBFactory | undefined;
  crypto?: Crypto | undefined;
  isSecureContext?: boolean;
}

/**
 * Wählt den sicheren Speicher: Android-Keystore für den Zugang, wenn die Bridge ihn anbietet,
 * sonst WebCrypto + IndexedDB (nur im sicheren Kontext), sonst null (Aktivierung V2 nicht möglich).
 * In der Android-Hülle liegt die Sitzung im WebView-Speicher; fehlt der, nur im Arbeitsspeicher
 * (ein Neustart während der Aktivierung beginnt dann neu).
 */
export function waehleSicherenSpeicher(u: SpeicherUmgebung): SichererSpeicher | null {
  const c = u.crypto;
  const web =
    u.isSecureContext !== true || u.indexedDB === undefined || c?.subtle === undefined
      ? null
      : new WebCryptoSpeicher(new IndexedDbAblage(u.indexedDB), c.subtle, (n) =>
          c.getRandomValues(new Uint8Array(n)),
        );
  if (istAndroidZugangBridge(u.BilanxGeraet)) {
    return new AndroidKeystoreSpeicher(u.BilanxGeraet, web ?? new ArbeitsspeicherTresor());
  }
  return web;
}
